import { UnitSystem } from 'providers/api';
import { distanceImperialToMetric } from 'utils/unitConversions';
import DxfParser from './src/index';
import { LineEntity } from './src/parser/entities/line';
import { LWPolylineEntity } from './src/parser/entities/lwpolyline';
import { PolylineEntity } from './src/parser/entities/polyline';
import { CommonDxfEntity } from './src/parser/entities/shared';
import { PCNode } from '../MineModel/Nodes';
import Vector from '../MineModel/Nodes/Vector';
import { SpoolEdge } from '../EditorPanelPage/utils/nodeCreation';
import { dedupeEdges, ImportGraph, resolveIntersectionsAndSplitLines } from '../EditorPanelPage/hooks/sharedImportUtils';

function isValidVertice(vertice: any) {
    return vertice.x !== undefined && vertice.y !== undefined && vertice.z !== undefined;
}

function isValidLWVertice(vertice: any) {
    return vertice.x !== undefined && vertice.y !== undefined;
}

const toMetric = (value: number, dxfUnitSystem: UnitSystem): number => (dxfUnitSystem === UnitSystem.Imperial
    ? distanceImperialToMetric(value)
    : value);

// Vertices are shared between entities by coordinate, so a node is only minted the first time a
// position is seen. `layer` seeds the pipe; the importer overwrites it with the model's default before
// the nodes are previewed.
const nodeAtCoords = (
    nodeMap: Map<string, PCNode>,
    coords: Vector,
    id: string,
    layer: string,
): PCNode => {
    const key = `${coords.x},${coords.y},${coords.z}`;
    const existing = nodeMap.get(key);
    if (existing) return existing;

    const node = new PCNode({ coords, id, pipeId: layer });
    nodeMap.set(key, node);
    return node;
};

function processPolyline(obj: PolylineEntity, nodeMap: Map<string, PCNode>, dxfUnitSystem: UnitSystem): SpoolEdge[] {
    const edges: SpoolEdge[] = [];
    let previousNode: PCNode | undefined;

    obj.vertices.forEach((vertice) => {
        if (!isValidVertice(vertice)) {
            return;
        }

        const coords = new Vector(
            toMetric(vertice.x, dxfUnitSystem),
            toMetric(vertice.y, dxfUnitSystem),
            toMetric(vertice.z, dxfUnitSystem),
        );

        const node = nodeAtCoords(nodeMap, coords, vertice.handle, obj.layer);

        if (previousNode && previousNode.id !== node.id) {
            edges.push({ nodeAId: previousNode.id, nodeBId: node.id });
        }

        previousNode = node;
    });

    return edges;
}

function processLWPolyline(obj: LWPolylineEntity, nodeMap: Map<string, PCNode>, dxfUnitSystem: UnitSystem): SpoolEdge[] {
    const edges: SpoolEdge[] = [];
    let previousNode: PCNode | undefined;

    obj.vertices.forEach((vertice, index) => {
        if (!isValidLWVertice(vertice)) {
            return;
        }

        const coords = new Vector(
            toMetric(vertice.x, dxfUnitSystem),
            toMetric(vertice.y, dxfUnitSystem),
            toMetric(obj.elevation, dxfUnitSystem),
        );

        const node = nodeAtCoords(nodeMap, coords, `${obj.handle ?? 'lwp'}_${index}`, obj.layer);

        if (previousNode && previousNode.id !== node.id) {
            edges.push({ nodeAId: previousNode.id, nodeBId: node.id });
        }

        previousNode = node;
    });

    return edges;
}

function extractNodeFromLineObject(obj: LineEntity, nodeMap: Map<string, PCNode>, dxfUnitSystem: UnitSystem): SpoolEdge[] {
    const edges: SpoolEdge[] = [];

    const startCoords = new Vector(
        toMetric(obj.startPoint.x, dxfUnitSystem),
        toMetric(obj.startPoint.y, dxfUnitSystem),
        toMetric(obj.startPoint.z, dxfUnitSystem),
    );
    const endCoords = new Vector(
        toMetric(obj.endPoint.x, dxfUnitSystem),
        toMetric(obj.endPoint.y, dxfUnitSystem),
        toMetric(obj.endPoint.z, dxfUnitSystem),
    );

    const startNode = nodeAtCoords(nodeMap, startCoords, `start_${obj.handle}`, obj.layer);
    const endNode = nodeAtCoords(nodeMap, endCoords, `end_${obj.handle}`, obj.layer);

    if (startNode.id !== endNode.id) {
        edges.push({ nodeAId: startNode.id, nodeBId: endNode.id });
    }

    return edges;
}

// A DXF entity contributes nodes at its vertices and one edge per consecutive pair. The edges are
// collected in their own list rather than as a back-reference on each node, because an edge is a spool
// and carries its own pipe.
function generateGraph(objects: CommonDxfEntity[], dxfUnitSystem: UnitSystem): ImportGraph {
    const nodeMap: Map<string, PCNode> = new Map();

    const edges = objects.reduce<SpoolEdge[]>((acc, obj) => {
        if (obj.type === 'POLYLINE') {
            return [...acc, ...processPolyline(obj as PolylineEntity, nodeMap, dxfUnitSystem)];
        }
        if (obj.type === 'LINE') {
            return [...acc, ...extractNodeFromLineObject(obj as LineEntity, nodeMap, dxfUnitSystem)];
        }
        if (obj.type === 'LWPOLYLINE') {
            return [...acc, ...processLWPolyline(obj as LWPolylineEntity, nodeMap, dxfUnitSystem)];
        }
        return acc;
    }, []);

    return { nodes: Array.from(nodeMap.values()), edges: dedupeEdges(edges) };
}

async function processDxfToJson(dxfContent: string, dxfUnitSystem: UnitSystem): Promise<ImportGraph> {
    try {
        const parser = new DxfParser();
        const dxfData = parser.parseSync(dxfContent);
        const objects = Array.isArray(dxfData.entities) ? dxfData.entities : [];

        return resolveIntersectionsAndSplitLines(generateGraph(objects, dxfUnitSystem), 0.5);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error processing DXF file:', error);
    }
    return { nodes: [], edges: [] };
}

export default { processDxfToJson };
